import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const HealthcareAndBiotechSolutions = () => {
  const phrases = [
    "FDA-Compliant Clinical Pipelines",
    "Real-Time Health Telemetry Sync",
    "AI Bioreactor Yield Modeling"
  ];

  const cardData = [
    { type: 'health-core', content: '🧬' },
    { type: 'safety-teal', content: '🏥' },
    { type: 'telemetry-stack', content: '📊' },
    { type: 'clinical-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🧬", title: "Laboratory Information Management (LIMS) Setup", desc: "Integrate molecular research layers directly with secure cloud networks to track biospecimen consumption, assay routing, and clinical-in-progress statuses instantly." },
    { icon: "⚡", title: "IoT Medical Device Telemetry Architecture", desc: "Connect patient monitoring arrays and laboratory diagnostics securely using HL7 and FHIR protocols to capture microsecond heart rate, oxygen, and therapeutic diagnostics." },
    { icon: "⚙️", title: "AI Predictive Diagnostic Systems", desc: "Deploy automated machine learning pipelines to detect anomalous genomic trends, forecast metabolic pathway decay, and prevent costly therapeutic timeline overruns." },
    { icon: "📊", title: "Clinical Trial Patient Retention Lenses", desc: "Ingest and process screening logs into clean operational metrics tracking cohort performance, drop-out rates, and clinical data submission margins." },
    { icon: "🔗", title: "Cold Chain Logistics & Specimen Synchronization", desc: "Unify pharmaceutical supply schedules, multi-location cryogenic warehouse operations, automated bill of molecular materials, and drug formulation frameworks." },
    { icon: "🛡️", title: "HIPAA CyberSecurity & Zero-Trust Architecture", desc: "Isolate clinical data storage environments from public networks using strict network segmentation, secure edge firewalls, and encrypted data brokers." },
    { icon: "🔄", title: "Laboratory Equipment Compliance & Calibration Management", desc: "Automate corrective repair tickets, sterile parts inventory alerts, and routine laboratory spectrometer calibration matrices based on runtime hours." }
  ];

  const advancedServices = [
    { icon: "🔍", title: "Computer Vision Automated Pathology Screening", desc: "Deploy high-speed digital microscopy networks running convolutional neural patterns to identify tissue micro-defects right during the diagnostic workflow." },
    { icon: "💼", title: "In-Silico Cell & Organ Digital Twin Modeling", desc: "Construct functional virtual mirrors of complete metabolic biological structures to simulate drug-cell interactions prior to executing clinical trials." },
    { icon: "🗃️", title: "Biomaterial Sourcing & Sustainability Tuning", desc: "Track power footprint spikes across high-capacity cryogenic preservation blocks to optimize storage run intervals and reduce base research utility costs." },
    { icon: "⚖️", title: "Automated FDA GxP Compliance & Track-and-Trace", desc: "Architect serialization frameworks recording immutable drug production and handling histories, ensuring strict patient safety and regulatory compliance." },
    { icon: "🚨", title: "Sterile Cleanroom Robotics & AGV Orchestration", desc: "Interface modern automated guided vehicles and smart chemical picking infrastructure directly into sterile reagent staging and storage bays." },
    { icon: "📝", title: "EHR/EMR Bi-Directional System Integrations", desc: "Bridge Electronic Health Records directly into patient diagnostic pipelines to eliminate clinical coordination and treatment variation lag." },
    { icon: "🌐", title: "Edge Compute Gateway Hospital Implementations", desc: "Deploy highly ruggedized compute boxes directly on critical care telemetry setups to clean, compress, and analyze vitals locally before cloud uploads." },
    { icon: "🧪", title: "Advanced Bioreactor Recipe & Batch Management", desc: "Implement strict, formula-driven sequencing logic with zero-fault tolerances across complex cell cultures, vaccines, or heavy biopharmaceutical processing lines." },
    { icon: "📦", title: "Dynamic Treatment Capacity & Pandemic Forecasting", desc: "Synthesize global patient incident velocity markers with real-time trial pipelines to adjust active treatment production volume schedules dynamically." }
  ];

  const technologies = {
    "Clinical Systems (LIMS/EHR)": ["Epic / Cerner EHR Connectors", "LabWare / Benchling LIMS Engines", "HL7 v2/v3 & FHIR Data Standards", "DICOM Medical Imaging Libraries"],
    "Bio-Telemetry & Analytics": ["AWS IoT for Healthcare", "Azure Health Data Consortium", "Time-Series Databases (InfluxDB)", "Power BI Patient Journey Lenses"],
    "Enterprise Lifesciences": ["SAP Life Sciences Batch Nodes", "Veeva Vault Clinical Document Sync", "Salesforce Health Cloud Portals", "Custom EDC / CDISC Pipeline Bridges"],
    "Bio-Edge & AI Infrastructure": ["NVIDIA Clara Imaging Edge Blocks", "TensorFlow Biomolecular Models", "Ruggedized Sterile Edge Gateways", "AWS Bedrock Medical Cohort RAG"],
    "Operational Compliance": ["Qualio / MasterControl QMS Suites", "FDA 21 CFR Part 11 Audit Loggers", "Sterile Chain-of-Custody Systems", "ISO 13485 Medical Device Gates"]
  };

  const processSteps = [
    { num: "01", title: "Clinical Workflow & Security Auditing", desc: "Mapping medical telemetry endpoints, identifying air-gapped laboratory networks, and assessing historical patient data delivery lag." },
    { num: "02", title: "Device Network & FHIR Blueprinting", desc: "Structuring secure VLAN partitions, specifying clinical telemetry tag directories, and mapping patient data payload structures." },
    { num: "03", title: "Middleware Integration & Ingestion Synthesis", desc: "Configuring ingestion networks, building clinical normalization structures, and writing initial automated diagnostic routing logic." },
    { num: "04", title: "EMR Integration & Data Mapping", desc: "Linking lab execution streams directly to patient charts, billing layers, and laboratory supply vendor lines." },
    { num: "05", title: "Clinical Validation & Cyber Verification", desc: "Verifying diagnostic model accuracy, testing localized hospital gateway failovers, and checking HIPAA zero-trust network block limits." },
    { num: "06", title: "Live Clinical Go-Live & Pipeline Sync", desc: "Activating real-time clinical data ingestion matrices across active laboratory sectors with zero interruption to active patient care cycles." },
    { num: "07", title: "Continuous Optimization Loops", desc: "Reviewing pipeline processing speed metrics, reducing telemetry traffic bottlenecks, and updating diagnostic AI pattern models regularly." }
  ];

  const industries = [
    "High-Volume Bio-Pharma & Vaccine Production Plants", "Cross-Border Specialized Genomic Research Labs", "In-Vitro & In-Vivo Diagnostic Medical Device Builders",
    "High-Density Consumer Health Tracking & Smart Wearables", "Specialized Sterile Cold Chain & Cryogenic Delivery", "Critical Care & Regional Acute Hospital Networks",
    "High-Scale Sterile Cleanroom Microfluidic Fab Nodes", "Public Health Monitoring Systems & Biosurveillance", "Prosthetics, Orthotics & Orthopedic Bio-Fabrication",
    "Specialized Blood Transfusion & Biospecimen Banks", "Contract Development & Manufacturing (CDMO) Hubs", "Translational Medicine & Academic Medical Centers"
  ];

  const designBenefits = [
    "Zero Specimen Reconciliation Gaps Across Clinical Labs", "Up to 35% Reduction in Lab Equipment Calibration Outages", "100% HIPAA-Compliant, Encrypted Patient Data Networks",
    "Guaranteed 99.95% Medical Diagnostics Ingest Network Availability", "Drastic Reduction in Manual Ward Logging and Charting Tasks", "Highly Scalable Genomic Data & Biomarker Storage Ingestion",
    "Seamless Lab-to-Clinic Enterprise EMR System Integration", "Real-Time Patient Cohort & Clinical Operational Metrics"
  ];

  const whyChooseUs = [
    "Certified Clinical Automation Architects & Health Tech Strategists", "Proven Track Record Modernizing Critical Healthcare Facility Infrastructure", "Airtight Zero-Trust HIPAA and GDPR Security Governance Patterns",
    "Deep Competency in High-Throughput Time-Series Physiological Optimization", "Platform-Agnostic LIMS, EMR, and Diagnostic Device Interfacing", "Resilient High-Availability Hospital Local Edge Compute Deployments",
    "End-to-End Clinical Lifecycle Governance and System Validation", "Transparent Research Goals & Verifiable Clinical Testing Metrics", "High-Availability Integration Infrastructure SLA Guarantees",
    "Experienced Biotech Engineers Specializing in GxP/FDA System Matrices", "Iterative Proof-of-Concept Target Environment Trial Sprints", "100% Diagnostic Network Operations Stability and Safety Assurances"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Healthcare & Biotech Solutions | Healthcare IT Services & Digital Transformation | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Healthcare & Biotech Solutions | Healthcare IT Services & Digital Transformation | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. delivers Healthcare and Biotech Solutions, including healthcare ERP, hospital management systems, electronic health records (EHR/EMR), laboratory information management systems (LIMS), biotech software, healthcare analytics, compliance solutions, and digital transformation services."
        />

        <meta
          name="keywords"
          content="Healthcare Solutions, Biotech Solutions, Healthcare IT Services, Healthcare ERP, Hospital Management System, HMS Software, Electronic Health Records, EHR, EMR, Laboratory Information Management System, LIMS, Healthcare Analytics, Biotech Software, Digital Healthcare, Healthcare Compliance, Medical Software Development, Healthcare Automation, Healthcare Digital Transformation, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/healthcare-biotech-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Healthcare & Biotech Solutions | Healthcare IT Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Transform healthcare and biotech operations with Affsoft's Healthcare & Biotech Solutions. We provide healthcare ERP, EHR/EMR, LIMS, hospital management systems, healthcare analytics, compliance, and digital transformation services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/healthcare-biotech-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Healthcare & Biotech Solutions | Healthcare IT Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Modernize healthcare and biotech organizations with Affsoft IT Solutions. Our services include healthcare ERP, EHR/EMR, LIMS, hospital management systems, healthcare analytics, compliance, and digital transformation."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="healthcare-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0f172a' // Deep Slate fallback (suitable for healthcare/tech theme)
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Marquee Text */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Healthcare &nbsp; Biotechnology &nbsp; Lifesciences &nbsp; &amp; &nbsp;</span>
              <span>Clinical Enterprise Architectures </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-teal fs-3" style={{ color: '#0d9488' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Healthcare &amp; Biotech</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the clinical data siloes and technical lag that cause diagnostic delays. Our healthcare automation practice pairs modern patient IoT device integration with robust GxP-compliant LIMS execution architectures and bio-analytics to convert clinical research and care pipelines into high-efficiency strategic spaces.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Propose a Biotech Execution Plan</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#0d9488' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#0d9488' }}>Clinical Technology Optimization</span>
              <h2 className="fw-bold text-dark display-6">Deploy High-Throughput Diagnostics & Fully Integrated Research Pipelines</h2>
              <p className="text-muted mt-3">
                Isolated laboratory setups, manual clinical logs, and reactive calibration loops impact therapeutic development. At Affsoft IT Solutions Pvt. Ltd., we build secure, zero-downtime, and systematically validated health & biotechnology architectures.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#0d9488' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise Healthcare &amp; Biotech Transformation</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#0d9488' }}>Distributed Medical & Clinical Nodes</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Biotech Capabilities Built for High-Density Operations</h2>
              <p className="text-muted mt-3">
                We scale basic diagnostic logging systems into fully integrated, adaptive digital clinical environments. From deploying computer vision pathology scanners to managing bioreactor execution networks, our blueprints protect your continuous diagnostic stability.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#0d9488' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#0d9488' }}>Clinical Technology Matrix</span>
              <h2 className="fw-bold display-6">Medical Platforms, Regulations & Connectivity Frameworks We Standardize</h2>
              <p className="text-light-50">We configure, monitor, and safeguard distributed clinical automation matrices utilizing strict FDA-compliant validation rules.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#0d9488' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#0d9488' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Development Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Clinical Automation Blueprint</h2>
              <p className="text-muted">A structured, strictly governed lifecycle designed to safely transition diagnostic pipelines from isolated laboratories onto live operational enterprise networks.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#0d9488' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#0d9488' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Healthcare &amp; Biotech Spaces We Transform</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🩺 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine critical biochemical research expertise with modern clinical data engineering to guarantee highly predictable, compliant clinical behavior.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#0d9488' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default HealthcareAndBiotechSolutions;